/**
 * Dispatch: the aerodrome officer sends a team from the map, and the team is
 * told.
 *
 * A dispatch is a P1 work order raised for a named team and moved straight to
 * `dispatched` — in the register, so the work order screens, the dashboards
 * and the mobile app all see it at once — and a message to every person in
 * the team: an SMS, an email and an alert in the mobile app. The platform
 * keeps each message on an outbox row with how it went; one that could not go
 * says why (no number on file, no provider set up), rather than vanishing.
 *
 * The team is a dispatch group, keyed by the map's team key (RFF1, SWP1 …),
 * with the people in it and how to reach them.
 */
import { t } from "../i18n";

export interface DispatchMember {
  name: string;
  phone?: string;
  email?: string;
  /** the platform sign-in that receives the app alert */
  principalId?: string;
  employeeId?: string;
}

export interface DispatchGroup {
  key: string;
  name: string;
  kind?: string;
  members: DispatchMember[];
}

export interface DispatchInput {
  siteId: string;
  groupKey: string;
  groupName?: string;
  groupKind?: string;
  title: string;
  description?: string;
  zoneCode?: string;
  assetTag?: string;
  lon?: number;
  lat?: number;
  /** where, in words, for the message */
  where?: string;
}

export type DispatchChannel = "sms" | "email" | "app";
export type DispatchStatus =
  "queued" | "sent" | "failed" | "no_address" | "not_configured" | "waiting" | "delivered" | "read";

export interface DispatchNotification {
  id: string;
  recipient: string;
  channel: DispatchChannel;
  to?: string;
  status: DispatchStatus;
  error?: string;
  event?: string;
}

export interface DispatchSummary {
  people: number;
  sms: number;
  email: number;
  app: number;
  no_address: number;
}

export interface DispatchResult {
  workOrder: { id: string; number: string; status: string; crew?: string };
  notifications: DispatchNotification[];
  summary: DispatchSummary;
  /** whether SMS and email are set up to go; without a provider they wait in the outbox */
  channels?: { sms: boolean; email: boolean };
}

export function summarise(rows: readonly DispatchNotification[]): DispatchSummary {
  const out: DispatchSummary = {
    people: new Set(rows.map((r) => r.recipient)).size,
    sms: 0,
    email: 0,
    app: 0,
    no_address: 0,
  };
  for (const r of rows) {
    if (r.status === "no_address") out.no_address++;
    else out[r.channel]++;
  }
  return out;
}

/** One line for the officer: who was told, how, and who was missed. */
export function dispatchLine(r: DispatchResult): string {
  const s = r.summary;
  const line = t("{number} sent to {team} — {people} people: {sms} SMS, {email} email, {app} app alerts", {
    number: r.workOrder.number,
    team: r.workOrder.crew ?? t("the team"),
    people: s.people,
    sms: s.sms,
    email: s.email,
    app: s.app,
  });
  /* a person none of whose three ways reached them, and a person only missing the app alert, are different things */
  const byPerson = new Map<string, DispatchNotification[]>();
  for (const n of r.notifications) byPerson.set(n.recipient, [...(byPerson.get(n.recipient) ?? []), n]);
  const nobody = [...byPerson.values()].filter((rows) => rows.every((x) => x.status === "no_address")).length;
  const noApp = [...byPerson.values()].filter(
    (rows) =>
      rows.some((x) => x.channel === "app" && x.status === "no_address") &&
      !rows.every((x) => x.status === "no_address"),
  ).length;
  const extra = [
    nobody ? t("{n} could not be reached (no contact on file)", { n: nobody }) : null,
    noApp ? t("{n} not signed in to the mobile app", { n: noApp }) : null,
  ].filter(Boolean);
  return extra.length ? `${line}; ${extra.join("; ")}` : line;
}
